import { QN_ARTICLE_COUNT_PER_PAGE } from "@config/QuickNewsConstants";
import logger from "@src/logger/Logger";
import { LatestVideoResponse } from "@transformer/useBrightcoveResponse";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import { MoreArticlesRequestData } from "@typings/MoreArticlesApi";
import {
  AdsCardProps,
  FeedItem,
  FinishCardProps,
  QNCardType,
  QuizCardProps,
  VideoItem,
} from "@typings/QuickNews";
import { useCallback, useMemo, useState } from "react";

import { getMoreFeeds, getMoreGallery, getMoreVideos } from "./QnLoadMore";

// --------------------------- Constants -------------------------------
export const END_CARD: FinishCardProps[] = [
  {
    id: `feed-end-success`,
    title: "Quick News Feed ended",
    name: "Quick News Feed ended",
    endCard: "Feeds Ended",
  },
];

const QUIZ_ITEM: QuizCardProps = {
  id: "tamil-quiz",
  title: "முரசு சொல்லாட்டம்",
  quizzes: "Random 10 Quizzes",
};

// --------------------------- Ad Helpers ------------------------------
const generateAdItems = (count: number): AdsCardProps[] =>
  Array.from({ length: count }, (_, i) => ({
    id: `imu2-card-${i + 1}`,
    adIndex: i + 1,
    title: "IMU2",
    name: "IMU2",
    adType: "IMU2",
  }));

export const insertAds = (items: FeedItem[]): FeedItem[] => {
  const withAds: FeedItem[] = [];
  const adItems = generateAdItems(Math.ceil(items.length / 4));

  items.forEach((item, index) => {
    withAds.push(item);
    if ((index + 1) % 3 === 0 && adItems.length > 0) {
      withAds.push(adItems.shift() as AdsCardProps);
    }
  });

  return withAds;
};

// ------------------------- Infinite Content Hook ---------------------
interface UseInfiniteContentReturn {
  content: FeedItem[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  isInitialized: boolean;
  loadMoreOlder: () => void;
}

export const useInfiniteContent = ({
  Stories,
  photoCards,
  initialActiveTab,
  initialId,
}: {
  Stories: ProcessedArticleData[];
  photoCards: ProcessedArticleData[];
  initialActiveTab: QNCardType;
  initialId?: string;
}): UseInfiniteContentReturn => {
  const initialContent = useMemo(() => {
    try {
      const merged = [...Stories];
      if (photoCards.length) {
        const randomIndex = 3 + Math.floor(Math.random() * 2); // 4th or 5th position
        merged.splice(randomIndex, 0, ...photoCards);
      }
      return insertAds(merged);
    } catch (err) {
      logger.error("Error computing initial content");
      return [];
    }
  }, [Stories, photoCards]);

  const [articles, setArticles] = useState<ProcessedArticleData[]>(Stories);
  const [galleries, setGalleries] =
    useState<ProcessedArticleData[]>(photoCards);
  const [content, setContent] = useState<FeedItem[]>(initialContent);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadMoreCount, setLoadMoreCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [quizAdded, setQuizAdded] = useState(false);
  const [isInitialized] = useState(true);
  const [loadOlderArticles, setLoadOlderArticles] = useState(false);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    setError(null);

    try {
      const newLoadMoreCount = loadMoreCount + 1;
      setLoadMoreCount(newLoadMoreCount);

      const lastArticle = articles.at(-1);
      const requestData: MoreArticlesRequestData = { sort: lastArticle?.sort };

      const newArticles = await getMoreFeeds(requestData, loadOlderArticles);
      setArticles((prev) => [...prev, ...newArticles]);

      let newGalleries: ProcessedArticleData[] = [];
      if (newLoadMoreCount % 2 === 0) {
        const lastGallery = galleries.at(-1);
        const requestGalleryData: MoreArticlesRequestData = {
          sort: lastGallery?.sort,
        };
        newGalleries = await getMoreGallery(requestGalleryData);
        setGalleries((prev) => [...prev, ...newGalleries]);
      }

      setHasMore(newArticles.length === QN_ARTICLE_COUNT_PER_PAGE);

      let combinedItems: FeedItem[] = [...newArticles];
      if (newGalleries.length > 0) {
        const randomIndex = Math.floor(
          Math.random() * (newArticles.length + 1),
        );
        combinedItems.splice(randomIndex, 0, ...newGalleries);
      }

      // Exclude initial active item
      const excludedId =
        initialActiveTab === "MyFeed" && initialId ? initialId : undefined;
      if (excludedId)
        combinedItems = combinedItems.filter((item) => item.id !== excludedId);

      setContent((prevContent) => {
        const updatedContent = prevContent.filter(
          (item) => !("adType" in item),
        );
        updatedContent.push(...combinedItems);

        if (newLoadMoreCount === 1 && !quizAdded) {
          const randomPosition = 5 + Math.floor(Math.random() * 6); // 5th-10th
          updatedContent.splice(randomPosition, 0, QUIZ_ITEM);
          setQuizAdded(true);
        }

        return insertAds(updatedContent);
      });
      if (newArticles.length < QN_ARTICLE_COUNT_PER_PAGE)
        setContent((prev) => [...prev, ...END_CARD]);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load more content",
      );
    } finally {
      setLoading(false);
    }
  }, [
    loading,
    hasMore,
    loadMoreCount,
    quizAdded,
    initialActiveTab,
    initialId,
    articles,
    galleries,
    loadOlderArticles,
  ]);

  const loadMoreOlder = useCallback(() => {
    setLoadOlderArticles(true);
    setHasMore(true);
  }, []);

  return {
    content,
    loading,
    error,
    hasMore,
    loadMore,
    isInitialized,
    loadMoreOlder,
  };
};

// ------------------------- Infinite Videos Hook ---------------------
interface UseInfiniteVideosReturn {
  videos: VideoItem[];
  loading: boolean;
  error: string | null;
  hasMoreVideos: boolean;
  loadMoreVideos: () => Promise<void>;
}

export const useInfiniteVideos = ({
  bcVideos,
  initialId,
  initialActiveTab,
}: {
  bcVideos?: LatestVideoResponse[];
  initialId?: string;
  initialActiveTab: QNCardType;
}): UseInfiniteVideosReturn => {
  const [videos, setVideos] = useState<VideoItem[]>(bcVideos || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadMoreCount, setLoadMoreCount] = useState(0);
  const [hasMoreVideos, setHasMoreVideos] = useState(true);

  const loadMoreVideos = useCallback(async () => {
    if (loading || !hasMoreVideos) return;

    setLoading(true);
    setError(null);

    try {
      const newLoadMoreCount = loadMoreCount + 1;
      setLoadMoreCount(newLoadMoreCount);

      const offset = videos.length;
      const newVideos = await getMoreVideos(offset);

      const excludedId = initialActiveTab === "Videos" ? initialId : undefined;
      const filteredVideos = excludedId
        ? newVideos.filter((v) => v.id !== excludedId)
        : newVideos;

      setVideos((prev) => [...prev, ...filteredVideos]);
      if (!newVideos.length) setVideos((prev) => [...prev, ...END_CARD]);
      setHasMoreVideos(newVideos.length > 0);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load more videos",
      );
    } finally {
      setLoading(false);
    }
  }, [
    loading,
    hasMoreVideos,
    loadMoreCount,
    initialId,
    initialActiveTab,
    videos,
  ]);

  return { videos, loading, error, hasMoreVideos, loadMoreVideos };
};
